import { StatusModal } from '@/components/StatusModal';
import type { ProductionTerm } from '@/components/vyroba/types';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useCallback, useEffect, useRef, useState } from 'react';

/** `sold: null` = ještě nedonačteno (živý Shoptet dotaz dorazí zvlášť, viz `loadSold`). */
type Variant = { variant_name: string | null; qty: number; sold: number | null };

type Produkt = {
  product_code: string;
  product_name: string;
  image_url: string | null;
  total_qty: number;
  variants: Variant[];
  note: string | null;
  fabric: string;
  naplet: { barva: string; bg: string; fg: string } | null;
};

/** Tvar hlavního endpointu — bez `sold`, ten se donačítá samostatně. */
type ProduktBez = Omit<Produkt, 'variants'> & {
  variants: { variant_name: string | null; qty: number }[];
};

const WEEKDAYS = ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota'];

/** 'YYYY-MM-DD' → 'Pondělí' (nad datum v tabu). */
function fmtWeekday(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return '';
  return WEEKDAYS[new Date(Number(y), Number(m) - 1, Number(d)).getDay()] ?? '';
}

/** 'YYYY-MM-DD' → 'D.M.' (krátce, pro taby). */
function fmtShort(iso: string): string {
  const [, m, d] = iso.split('-');
  return d && m ? `${Number.parseInt(d, 10)}.${Number.parseInt(m, 10)}.` : iso;
}

/** 'YYYY-MM-DD' → 'Pondělí 27.7.2026'. */
function fmtFull(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  const wd = WEEKDAYS[new Date(Number(y), Number(m) - 1, Number(d)).getDay()] ?? '';
  return `${wd} ${Number(d)}.${Number(m)}.${y}`;
}

/**
 * Vyskladnění z výroby — stejné termíny a karty jako plán stříhání, ale kusy
 * se odepisují. Do skladu ani do e-shopu se nic nepřičítá, jen se sníží
 * evidence výroby a přepočítá dostupnost varianty.
 */
export function VyskladneniPage() {
  const [terms, setTerms] = useState<ProductionTerm[]>([]);
  // Kolik toho na termín zbývá — podle toho se prázdné taby schovají.
  const [naplnenost, setNaplnenost] = useState<Record<string, number>>({});
  const [activeTerm, setActiveTerm] = useState<ProductionTerm | null>(null);
  const [products, setProducts] = useState<Produkt[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);
  // Otevřený modal „kolik kusů" pro jednu velikost.
  const [target, setTarget] = useState<{ p: Produkt; v: Variant } | null>(null);
  // Potvrzení hromadného odpisu celé karty (vše / jen neprodané).
  const [confirmAll, setConfirmAll] = useState<{ p: Produkt; unsold: boolean } | null>(null);

  const today = new Date().toISOString().slice(0, 10);
  // Termíny řazené podle data — i ty PO termínu, kde ještě něco zbývá.
  // Zaostávající resty se nesmí tiše ztratit z dohledu.
  const seRazene = [...terms].sort((a, b) => a.term_date.localeCompare(b.term_date));
  // Termíny bez zůstatku se v záložkách vůbec neukazují.
  const tabs = seRazene.filter(
    (t) => (naplnenost[t.term_date] ?? 0) > 0 || activeTerm?.id === t.id,
  );

  useEffect(() => {
    Promise.all([
      apiFetch<{ terms: ProductionTerm[] }>('/api/vyroba/terms'),
      apiFetch<{ terms: { term_date: string; qty: number }[] }>(
        '/api/vyroba/striharna/vyskladneni/terminy',
      ).catch(() => ({ terms: [] })),
    ])
      .then(([d, prehled]) => {
        setTerms(d.terms);
        const obsazeno: Record<string, number> = {};
        for (const p of prehled.terms) obsazeno[p.term_date] = p.qty;
        setNaplnenost(obsazeno);
        const t = new Date().toISOString().slice(0, 10);
        const serazene = [...d.terms].sort((a, b) => a.term_date.localeCompare(b.term_date));
        const vybrany =
          serazene.find((x) => (obsazeno[x.term_date] ?? 0) > 0) ??
          serazene.find((x) => x.term_date >= t) ??
          serazene[0] ??
          null;
        if (vybrany) setActiveTerm(vybrany);
      })
      .catch((e: unknown) =>
        setToast({
          msg: e instanceof ApiError ? e.describe() : 'Chyba načítání termínů',
          kind: 'error',
        }),
      );
  }, []);

  // Kvůli rychlému přeskakování mezi taby — donačtená "prodáno" data se smí
  // sloučit jen do produktů termínu, na který se pořád ještě díváme.
  const activeTermRef = useRef<ProductionTerm | null>(null);
  useEffect(() => {
    activeTermRef.current = activeTerm;
  }, [activeTerm]);

  /**
   * Prodané kusy — samostatný, pomalejší dotaz (živý Shoptet nárok na každé
   * SKU). Karty se vykreslí bez čekání na tohle; čísla "prodáno" se sloučí
   * do stavu, jakmile dorazí.
   */
  const loadSold = useCallback(async (term: ProductionTerm) => {
    try {
      const d = await apiFetch<{ sold: Record<string, number> }>(
        `/api/vyroba/striharna/vyskladneni/prodano?term_date=${term.term_date}`,
      );
      if (activeTermRef.current?.term_date !== term.term_date) return;
      setProducts((prev) =>
        prev
          ? prev.map((p) => ({
              ...p,
              variants: p.variants.map((v) => ({
                ...v,
                sold: d.sold[`${p.product_code}|${v.variant_name ?? ''}`] ?? 0,
              })),
            }))
          : prev,
      );
    } catch {
      // Tichá chyba — karty zůstanou, jen "prodáno" zůstane nedonačtené.
    }
  }, []);

  /**
   * `silent` = po akci (vyskladnění) — dotáhne data na pozadí bez zablikání
   * "Načítám…" a bez zbourání gridu, ať se neztrácí scroll pozice.
   */
  const load = useCallback(
    async (term: ProductionTerm, opts?: { silent?: boolean }) => {
      if (!opts?.silent) setProducts(null);
      try {
        const d = await apiFetch<{ products: ProduktBez[] }>(
          `/api/vyroba/striharna/vyskladneni?term_date=${term.term_date}`,
        );
        setProducts(
          d.products.map((p) => ({
            ...p,
            variants: p.variants.map((v) => ({ ...v, sold: null })),
          })),
        );
      } catch (e: unknown) {
        if (!opts?.silent) setProducts([]);
        setToast({
          msg: e instanceof ApiError ? e.describe() : 'Chyba načítání produktů',
          kind: 'error',
        });
        return;
      }
      void loadSold(term);
    },
    [loadSold],
  );

  useEffect(() => {
    if (activeTerm) load(activeTerm);
  }, [activeTerm, load]);

  /** Kolik kusů produktu v termínu nikdo nemá objednané — null, dokud se "prodáno" nedonačte. */
  function neprodanych(p: Produkt): number | null {
    if (p.variants.some((v) => v.sold == null)) return null;
    return p.variants.reduce((sum, v) => sum + Math.max(0, v.qty - (v.sold ?? 0)), 0);
  }

  async function vyskladnit(
    p: Produkt,
    v: Variant | null,
    qty?: number,
    mode: 'all' | 'unsold' = 'all',
  ) {
    if (!activeTerm) return;
    setBusy(true);
    try {
      await apiFetch('/api/vyroba/striharna/vyskladnit', {
        method: 'POST',
        body: {
          term_date: activeTerm.term_date,
          product_code: p.product_code,
          ...(v
            ? { variant_name: v.variant_name ?? '', qty }
            : mode === 'unsold'
              ? { unsold: true }
              : { all: true }),
        },
      });
      setToast({
        msg: v
          ? `Vyskladněno ${qty} ks — ${p.product_code} ${v.variant_name ?? ''}`.trim()
          : mode === 'unsold'
            ? `Vyskladněny neprodané kusy — ${p.product_code}`
            : `Vyskladněn celý ${p.product_code}`,
        kind: 'success',
      });
      setTarget(null);
      setConfirmAll(null);
      await load(activeTerm, { silent: true });
    } catch (e: unknown) {
      setToast({
        msg: e instanceof ApiError ? e.describe() : 'Vyskladnění selhalo',
        kind: 'error',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">Vyskladnění z výroby</h1>
        <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
          Odepsání kusů z evidence výroby — když se dávka ruší nebo se spletlo zadání. Do skladu ani
          do e-shopu se nic nepřičítá, jen se přepočítá dostupnost velikosti.
        </p>
      </div>

      {/* Taby s termíny */}
      {tabs.length > 0 ? (
        <div className="flex flex-wrap gap-2 mb-5">
          {tabs.map((t) => {
            const active = activeTerm?.id === t.id;
            const poTerminu = t.term_date < today;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  // Vymazat rovnou při kliku, ne až v efektu po přepnutí
                  // termínu — ať se skeleton objeví v okamžiku kliknutí.
                  setProducts(null);
                  setActiveTerm(t);
                }}
                className={`relative rounded-lg px-6 py-3 text-[17px] font-bold transition-colors cursor-pointer ${
                  active
                    ? 'bg-action text-white'
                    : poTerminu
                      ? 'bg-danger-light border border-danger/40 text-danger hover:border-danger'
                      : 'bg-bg border border-border text-text-secondary hover:border-text-muted'
                }`}
              >
                {poTerminu && (
                  <span className="absolute -top-2 -right-2 text-[10px] font-bold uppercase tracking-wide bg-danger text-white rounded px-1.5 py-0.5 whitespace-nowrap">
                    Po termínu
                  </span>
                )}
                <span className="flex flex-col leading-tight">
                  <span
                    className={`text-[14px] font-semibold ${active ? 'text-white/80' : poTerminu ? 'text-danger/80' : 'text-text-muted'}`}
                  >
                    {fmtWeekday(t.term_date)}
                  </span>
                  {fmtShort(t.term_date)}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-text-muted">Žádné aktivní termíny výroby.</p>
      )}

      {activeTerm && (
        <>
          <div className="bg-surface rounded-xl shadow-card px-5 py-4 mb-5">
            <h2 className="text-xl font-bold text-text">{fmtFull(activeTerm.term_date)}</h2>
          </div>

          {products == null ? (
            <ProductGridSkeleton />
          ) : products.length === 0 ? (
            <p className="text-sm text-text-muted px-1">
              K tomuto termínu není zadaný žádný produkt.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {products.map((p) => (
                <div key={p.product_code} className="bg-surface rounded-xl shadow-card p-5">
                  {p.note && (
                    <div className="mb-4 rounded-md border-l-4 border-warning bg-warning-light px-4 py-2.5 text-lg text-text whitespace-pre-wrap">
                      {p.note}
                    </div>
                  )}
                  <div className="flex gap-5 md:gap-3 lg:gap-5">
                    <div className="shrink-0">
                      {p.image_url ? (
                        <img
                          src={p.image_url}
                          alt=""
                          className="w-[104px] h-[104px] md:w-[88px] md:h-[88px] lg:w-[104px] lg:h-[104px] object-contain rounded-lg border border-border bg-white p-1"
                        />
                      ) : (
                        <div className="w-[104px] h-[104px] md:w-[88px] md:h-[88px] lg:w-[104px] lg:h-[104px] rounded-lg border border-border bg-bg" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="text-[15px] font-mono text-text-muted">
                        {p.product_code}
                      </span>
                      <div className="text-lg font-semibold text-text">{p.product_name}</div>
                      {p.fabric && (
                        <div className="mt-1 text-[15px]">
                          <span className="font-semibold text-text">Látka: </span>
                          <span className="text-text-secondary">{p.fabric}</span>
                        </div>
                      )}
                      {p.naplet && (
                        <div className="mt-1 text-[15px]">
                          <span className="font-semibold text-text">Náplet: </span>
                          <span
                            className="inline-block rounded-full px-2.5 py-0.5 text-sm font-semibold align-middle"
                            style={{ backgroundColor: p.naplet.bg, color: p.naplet.fg }}
                          >
                            {p.naplet.barva}
                          </span>
                        </div>
                      )}
                      <div className="mt-2.5">
                        {p.variants.map((v) => (
                          <div
                            key={v.variant_name ?? '__none__'}
                            className="flex items-center justify-between gap-3 border-b border-border-light py-1.5 text-base"
                          >
                            <span className="text-text">{v.variant_name ?? 'Bez velikosti'}</span>
                            <span className="flex items-center gap-3">
                              {v.sold == null ? (
                                <span className="inline-block h-4 w-20 rounded bg-warning-light animate-pulse" />
                              ) : (
                                <span className="text-[15px] font-semibold text-warning">
                                  prodáno {v.sold} ks
                                </span>
                              )}
                              <span className="font-semibold text-text">{v.qty} ks</span>
                              <button
                                type="button"
                                onClick={() => setTarget({ p, v })}
                                className="rounded-md border border-danger/40 bg-danger-light px-2.5 py-1 text-sm font-semibold text-danger hover:opacity-90 cursor-pointer transition-opacity"
                              >
                                Vyskladnit
                              </button>
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 text-[15px] text-text-muted">
                        Celkem {p.total_qty} ks
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <button
                      type="button"
                      onClick={() => setConfirmAll({ p, unsold: true })}
                      disabled={neprodanych(p) == null || neprodanych(p) === 0}
                      className="flex-1 rounded-md border border-danger/40 bg-danger-light px-4 py-2.5 text-[15px] font-bold text-danger hover:opacity-90 disabled:opacity-40 cursor-pointer transition-opacity"
                    >
                      Vyskladnit neprodané ({neprodanych(p) ?? '…'} ks)
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmAll({ p, unsold: false })}
                      className="flex-1 rounded-md bg-danger px-4 py-2.5 text-[15px] font-bold text-white hover:opacity-90 cursor-pointer transition-opacity"
                    >
                      Vyskladnit vše ({p.total_qty} ks)
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {target && (
        <QtyModal
          product={target.p}
          variant={target.v}
          busy={busy}
          onCancel={() => setTarget(null)}
          onSubmit={(qty) => vyskladnit(target.p, target.v, qty)}
        />
      )}

      {confirmAll && (
        <ConfirmAllModal
          product={confirmAll.p}
          unsold={confirmAll.unsold}
          count={confirmAll.unsold ? (neprodanych(confirmAll.p) ?? 0) : confirmAll.p.total_qty}
          busy={busy}
          onCancel={() => setConfirmAll(null)}
          onConfirm={() =>
            vyskladnit(confirmAll.p, null, undefined, confirmAll.unsold ? 'unsold' : 'all')
          }
        />
      )}

      {toast && (
        <StatusModal message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />
      )}
    </>
  );
}

/** Zástupné karty při přepnutí termínu — kopírují rozvržení skutečných karet. */
function ProductGridSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {Array.from({ length: 4 }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: statický počet, nic se nepřeuspořádává
        <div key={i} className="bg-surface rounded-xl shadow-card p-5 animate-pulse">
          <div className="flex gap-5">
            <div className="w-[104px] h-[104px] rounded-lg bg-bg shrink-0" />
            <div className="min-w-0 flex-1 space-y-2.5">
              <div className="h-3.5 w-20 rounded bg-bg" />
              <div className="h-5 w-3/4 rounded bg-bg" />
              <div className="h-3.5 w-1/2 rounded bg-bg" />
              <div className="mt-3 space-y-2">
                <div className="h-4 w-full rounded bg-bg" />
                <div className="h-4 w-full rounded bg-bg" />
              </div>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <div className="h-10 flex-1 rounded-md bg-bg" />
            <div className="h-10 flex-1 rounded-md bg-bg" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Kolik kusů velikosti odepsat. Výchozí je všechno, co v termínu zbývá. */
function QtyModal({
  product,
  variant,
  busy,
  onCancel,
  onSubmit,
}: {
  product: Produkt;
  variant: Variant;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (qty: number) => void;
}) {
  useEscapeKey(onCancel);
  const [value, setValue] = useState(String(variant.qty));
  const qty = Number.parseInt(value, 10);
  const valid = Number.isFinite(qty) && qty > 0 && qty <= variant.qty;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onCancel}
        className="fixed inset-0 bg-black/60 cursor-default"
      />
      <div className="relative w-full max-w-sm bg-surface rounded-2xl shadow-modal p-6 reveal">
        <h2 className="text-lg font-bold text-text">Vyskladnit z výroby</h2>
        <p className="text-sm text-text-secondary mt-1">
          {product.product_code} · {variant.variant_name ?? 'Bez velikosti'} — v termínu je{' '}
          {variant.qty} ks
        </p>
        <label htmlFor="vyskladnit-qty" className="block text-sm font-semibold text-text mt-4 mb-1">
          Počet kusů
        </label>
        <input
          id="vyskladnit-qty"
          type="number"
          min={1}
          max={variant.qty}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-full rounded-lg border border-border bg-bg px-3 py-2 text-lg font-bold text-text focus:outline-none focus:border-primary"
        />
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover cursor-pointer transition-colors"
          >
            Zrušit
          </button>
          <button
            type="button"
            disabled={!valid || busy}
            onClick={() => onSubmit(qty)}
            className="bg-danger text-white text-sm font-bold rounded-lg px-5 py-2.5 hover:opacity-90 disabled:opacity-50 cursor-pointer transition-opacity"
          >
            {busy ? 'Vyskladňuji…' : 'Vyskladnit'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Potvrzení odpisu celé karty — je to nevratná změna evidence. */
function ConfirmAllModal({
  product,
  unsold,
  count,
  busy,
  onCancel,
  onConfirm,
}: {
  product: Produkt;
  unsold: boolean;
  count: number;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useEscapeKey(onCancel);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onCancel}
        className="fixed inset-0 bg-black/60 cursor-default"
      />
      <div className="relative w-full max-w-sm bg-surface rounded-2xl shadow-modal p-6 reveal">
        <h2 className="text-lg font-bold text-text">
          {unsold ? 'Vyskladnit neprodané kusy?' : 'Vyskladnit celý produkt?'}
        </h2>
        <p className="text-sm text-text-secondary mt-1">
          {product.product_code} — z termínu se odepíše {count} ks
          {unsold ? '; prodané kusy zůstanou.' : ` (${product.variants.length} velikostí).`}
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover cursor-pointer transition-colors"
          >
            Zrušit
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className="bg-danger text-white text-sm font-bold rounded-lg px-5 py-2.5 hover:opacity-90 disabled:opacity-50 cursor-pointer transition-opacity"
          >
            {busy ? 'Vyskladňuji…' : unsold ? 'Vyskladnit neprodané' : 'Vyskladnit vše'}
          </button>
        </div>
      </div>
    </div>
  );
}
